/**
 * Theme service
 * Applies theme tokens to the document root and handles mode toggling.
 */

import type { ThemeApplyOptions, ThemeDefinition, ThemeTokens, ThemeMode } from './theme.types';
import { DEFAULT_LIGHT_THEME, DEFAULT_DARK_THEME, THEME_CSS_VARS } from './theme.constants';

const DEFAULT_ROOT = typeof document !== 'undefined' ? document.documentElement : undefined;

export function applyThemeTokens(theme: ThemeDefinition, options: ThemeApplyOptions = {}): void {
  const root = options.root ?? DEFAULT_ROOT;
  if (!root) {
    return;
  }

  const tokens = theme.tokens;

  applyColorTokens(root, tokens.colors);
  applyTypographyTokens(root, tokens.typography);
  applyRadiusTokens(root, tokens.radius);
  applySpacingTokens(root, tokens.spacing);
  applyShadowTokens(root, tokens.shadow);

  if (!options.preserveModeClass) {
    setThemeMode(options.mode ?? theme.mode, root);
  }
}

export function setThemeMode(
  mode: ThemeDefinition['mode'],
  root: HTMLElement | undefined = DEFAULT_ROOT
): void {
  if (!root) {
    return;
  }

  if (mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function mergeThemeTokens(base: ThemeTokens, overrides?: Partial<ThemeTokens>): ThemeTokens {
  if (!overrides) {
    return base;
  }

  return {
    colors: {
      ...base.colors,
      ...overrides.colors,
      card: {
        ...base.colors.card,
        ...overrides.colors?.card,
      },
      popover: {
        ...base.colors.popover,
        ...overrides.colors?.popover,
      },
      primary: {
        ...base.colors.primary,
        ...overrides.colors?.primary,
      },
      secondary: {
        ...base.colors.secondary,
        ...overrides.colors?.secondary,
      },
      muted: {
        ...base.colors.muted,
        ...overrides.colors?.muted,
      },
      accent: {
        ...base.colors.accent,
        ...overrides.colors?.accent,
      },
      destructive: {
        ...base.colors.destructive,
        ...overrides.colors?.destructive,
      },
      sidebar: {
        ...base.colors.sidebar,
        ...overrides.colors?.sidebar,
      },
      chart: {
        ...base.colors.chart,
        ...overrides.colors?.chart,
      },
      outline: {
        ...base.colors.outline,
        ...overrides.colors?.outline,
      },
      elevate: {
        ...base.colors.elevate,
        ...overrides.colors?.elevate,
      },
    },
    typography: {
      ...base.typography,
      ...overrides.typography,
      fontFamily: {
        ...base.typography.fontFamily,
        ...overrides.typography?.fontFamily,
      },
      fontSize: {
        ...base.typography.fontSize,
        ...overrides.typography?.fontSize,
      },
      lineHeight: {
        ...base.typography.lineHeight,
        ...overrides.typography?.lineHeight,
      },
      fontWeight: {
        ...base.typography.fontWeight,
        ...overrides.typography?.fontWeight,
      },
      letterSpacing: {
        ...base.typography.letterSpacing,
        ...overrides.typography?.letterSpacing,
      },
    },
    radius: {
      ...base.radius,
      ...overrides.radius,
    },
    spacing: {
      ...base.spacing,
      ...overrides.spacing,
    },
    shadow: {
      ...base.shadow,
      ...overrides.shadow,
      elevation: {
        ...base.shadow.elevation,
        ...overrides.shadow?.elevation,
      },
    },
  };
}

export function getDefaultTheme(mode?: ThemeMode): ThemeDefinition {
  return mode === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
}

export function validateThemeTokens(tokens: ThemeTokens): string[] {
  const missing: string[] = [];

  if (!tokens.colors?.background) missing.push('colors.background');
  if (!tokens.colors?.foreground) missing.push('colors.foreground');
  if (!tokens.colors?.border) missing.push('colors.border');
  if (!tokens.colors?.primary?.base) missing.push('colors.primary.base');
  if (!tokens.colors?.primary?.foreground) missing.push('colors.primary.foreground');
  if (!tokens.typography?.fontFamily?.sans) missing.push('typography.fontFamily.sans');
  if (!tokens.radius?.md) missing.push('radius.md');
  if (!tokens.spacing?.['4']) missing.push('spacing.4');
  if (!tokens.shadow?.md) missing.push('shadow.md');

  return missing;
}

function applyColorTokens(root: HTMLElement, tokens: ThemeTokens['colors']): void {
  setCssVar(root, THEME_CSS_VARS.colors.background, tokens.background);
  setCssVar(root, THEME_CSS_VARS.colors.foreground, tokens.foreground);
  setCssVar(root, THEME_CSS_VARS.colors.border, tokens.border);
  setCssVar(root, THEME_CSS_VARS.colors.input, tokens.input);
  setCssVar(root, THEME_CSS_VARS.colors.ring, tokens.ring);

  setCssVar(root, THEME_CSS_VARS.colors.card.base, tokens.card.base);
  setCssVar(root, THEME_CSS_VARS.colors.card.foreground, tokens.card.foreground);
  setCssVar(root, THEME_CSS_VARS.colors.card.border, tokens.card.border);

  setCssVar(root, THEME_CSS_VARS.colors.popover.base, tokens.popover.base);
  setCssVar(root, THEME_CSS_VARS.colors.popover.foreground, tokens.popover.foreground);
  setCssVar(root, THEME_CSS_VARS.colors.popover.border, tokens.popover.border);

  setCssVar(root, THEME_CSS_VARS.colors.primary.base, tokens.primary.base);
  setCssVar(root, THEME_CSS_VARS.colors.primary.foreground, tokens.primary.foreground);

  setCssVar(root, THEME_CSS_VARS.colors.secondary.base, tokens.secondary.base);
  setCssVar(root, THEME_CSS_VARS.colors.secondary.foreground, tokens.secondary.foreground);

  setCssVar(root, THEME_CSS_VARS.colors.muted.base, tokens.muted.base);
  setCssVar(root, THEME_CSS_VARS.colors.muted.foreground, tokens.muted.foreground);

  setCssVar(root, THEME_CSS_VARS.colors.accent.base, tokens.accent.base);
  setCssVar(root, THEME_CSS_VARS.colors.accent.foreground, tokens.accent.foreground);

  setCssVar(root, THEME_CSS_VARS.colors.destructive.base, tokens.destructive.base);
  setCssVar(root, THEME_CSS_VARS.colors.destructive.foreground, tokens.destructive.foreground);

  setCssVar(root, THEME_CSS_VARS.colors.sidebar.base, tokens.sidebar.base);
  setCssVar(root, THEME_CSS_VARS.colors.sidebar.foreground, tokens.sidebar.foreground);
  setCssVar(root, THEME_CSS_VARS.colors.sidebar.border, tokens.sidebar.border);
  setCssVar(root, THEME_CSS_VARS.colors.sidebar.primary, tokens.sidebar.primary);
  setCssVar(
    root,
    THEME_CSS_VARS.colors.sidebar.primaryForeground,
    tokens.sidebar.primaryForeground
  );
  setCssVar(root, THEME_CSS_VARS.colors.sidebar.accent, tokens.sidebar.accent);
  setCssVar(root, THEME_CSS_VARS.colors.sidebar.accentForeground, tokens.sidebar.accentForeground);
  setCssVar(root, THEME_CSS_VARS.colors.sidebar.ring, tokens.sidebar.ring);

  setCssVar(root, THEME_CSS_VARS.colors.chart['1'], tokens.chart['1']);
  setCssVar(root, THEME_CSS_VARS.colors.chart['2'], tokens.chart['2']);
  setCssVar(root, THEME_CSS_VARS.colors.chart['3'], tokens.chart['3']);
  setCssVar(root, THEME_CSS_VARS.colors.chart['4'], tokens.chart['4']);
  setCssVar(root, THEME_CSS_VARS.colors.chart['5'], tokens.chart['5']);

  setCssVar(root, THEME_CSS_VARS.colors.outline.button, tokens.outline.button);
  setCssVar(root, THEME_CSS_VARS.colors.outline.badge, tokens.outline.badge);
  setCssVar(root, THEME_CSS_VARS.colors.elevate['1'], tokens.elevate['1']);
  setCssVar(root, THEME_CSS_VARS.colors.elevate['2'], tokens.elevate['2']);
  setCssVar(
    root,
    THEME_CSS_VARS.colors.opaqueButtonBorderIntensity,
    tokens.opaqueButtonBorderIntensity
  );
}

function applyTypographyTokens(root: HTMLElement, tokens: ThemeTokens['typography']): void {
  setCssVar(root, THEME_CSS_VARS.typography.fontFamily.sans, tokens.fontFamily.sans);
  setCssVar(root, THEME_CSS_VARS.typography.fontFamily.serif, tokens.fontFamily.serif);
  setCssVar(root, THEME_CSS_VARS.typography.fontFamily.mono, tokens.fontFamily.mono);

  setCssVar(root, THEME_CSS_VARS.typography.fontSize.xs, tokens.fontSize.xs);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize.sm, tokens.fontSize.sm);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize.base, tokens.fontSize.base);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize.lg, tokens.fontSize.lg);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize.xl, tokens.fontSize.xl);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize['2xl'], tokens.fontSize['2xl']);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize['3xl'], tokens.fontSize['3xl']);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize['4xl'], tokens.fontSize['4xl']);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize['5xl'], tokens.fontSize['5xl']);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize['6xl'], tokens.fontSize['6xl']);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize['7xl'], tokens.fontSize['7xl']);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize['8xl'], tokens.fontSize['8xl']);
  setCssVar(root, THEME_CSS_VARS.typography.fontSize['9xl'], tokens.fontSize['9xl']);

  setCssVar(root, THEME_CSS_VARS.typography.lineHeight.xs, tokens.lineHeight.xs);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight.sm, tokens.lineHeight.sm);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight.base, tokens.lineHeight.base);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight.lg, tokens.lineHeight.lg);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight.xl, tokens.lineHeight.xl);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight['2xl'], tokens.lineHeight['2xl']);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight['3xl'], tokens.lineHeight['3xl']);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight['4xl'], tokens.lineHeight['4xl']);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight['5xl'], tokens.lineHeight['5xl']);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight['6xl'], tokens.lineHeight['6xl']);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight['7xl'], tokens.lineHeight['7xl']);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight['8xl'], tokens.lineHeight['8xl']);
  setCssVar(root, THEME_CSS_VARS.typography.lineHeight['9xl'], tokens.lineHeight['9xl']);

  setCssVar(root, THEME_CSS_VARS.typography.fontWeight.regular, tokens.fontWeight.regular);
  setCssVar(root, THEME_CSS_VARS.typography.fontWeight.medium, tokens.fontWeight.medium);
  setCssVar(root, THEME_CSS_VARS.typography.fontWeight.semibold, tokens.fontWeight.semibold);
  setCssVar(root, THEME_CSS_VARS.typography.fontWeight.bold, tokens.fontWeight.bold);

  setCssVar(root, THEME_CSS_VARS.typography.letterSpacing.tight, tokens.letterSpacing.tight);
  setCssVar(root, THEME_CSS_VARS.typography.letterSpacing.normal, tokens.letterSpacing.normal);
  setCssVar(root, THEME_CSS_VARS.typography.letterSpacing.wide, tokens.letterSpacing.wide);
}

function applyRadiusTokens(root: HTMLElement, tokens: ThemeTokens['radius']): void {
  setCssVar(root, THEME_CSS_VARS.radius.sm, tokens.sm);
  setCssVar(root, THEME_CSS_VARS.radius.md, tokens.md);
  setCssVar(root, THEME_CSS_VARS.radius.lg, tokens.lg);
  setCssVar(root, THEME_CSS_VARS.radius.xl, tokens.xl);
  setCssVar(root, THEME_CSS_VARS.radius['2xl'], tokens['2xl']);
  setCssVar(root, THEME_CSS_VARS.radius.full, tokens.full);
}

function applySpacingTokens(root: HTMLElement, tokens: ThemeTokens['spacing']): void {
  Object.entries(THEME_CSS_VARS.spacing).forEach(([key, cssVar]) => {
    const value = tokens[key as keyof ThemeTokens['spacing']];
    if (value) {
      setCssVar(root, cssVar, value);
    }
  });
}

function applyShadowTokens(root: HTMLElement, tokens: ThemeTokens['shadow']): void {
  setCssVar(root, THEME_CSS_VARS.shadow['2xs'], tokens['2xs']);
  setCssVar(root, THEME_CSS_VARS.shadow.xs, tokens.xs);
  setCssVar(root, THEME_CSS_VARS.shadow.sm, tokens.sm);
  setCssVar(root, THEME_CSS_VARS.shadow.base, tokens.base);
  setCssVar(root, THEME_CSS_VARS.shadow.md, tokens.md);
  setCssVar(root, THEME_CSS_VARS.shadow.lg, tokens.lg);
  setCssVar(root, THEME_CSS_VARS.shadow.xl, tokens.xl);
  setCssVar(root, THEME_CSS_VARS.shadow['2xl'], tokens['2xl']);

  setCssVar(root, THEME_CSS_VARS.shadow.elevation['1'], tokens.elevation['1']);
  setCssVar(root, THEME_CSS_VARS.shadow.elevation['2'], tokens.elevation['2']);
  setCssVar(root, THEME_CSS_VARS.shadow.elevation['3'], tokens.elevation['3']);
  setCssVar(root, THEME_CSS_VARS.shadow.elevation['4'], tokens.elevation['4']);
  setCssVar(root, THEME_CSS_VARS.shadow.elevation['5'], tokens.elevation['5']);

  setCssVar(root, THEME_CSS_VARS.shadow.primaryGlow, tokens.primaryGlow);
  setCssVar(root, THEME_CSS_VARS.shadow.primaryHover, tokens.primaryHover);
}

function setCssVar(root: HTMLElement, varName: string, value: string): void {
  root.style.setProperty(varName, value);
}
